import type { ScrollViewInstance, TextInputInstance } from 'react-native';
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Modal,
  StyleSheet,
  TouchableOpacity,
  Text,
  ScrollView,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
  Animated,
} from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import * as Sentry from '@sentry/react-native';
import { supabase, ensureDiscoverySession } from '../../lib/supabase';
import { trackFunnelEvent } from '../../lib/funnelEvents';
import { getCurrentJourneyId } from '../../lib/funnelJourney';
import { reportChatSessionCreationError } from '../../lib/chatSessionErrorReporting';
import { buildProjectLimitMessage, isProjectLimitReachedError, toProjectLimitReachedError, type ProjectLimitReachedError } from '../../lib/entitlements';
import ProjectCreationUpgradeBanner from '../billing/ProjectCreationUpgradeBanner';
import colors from '../../lib/colors';
import { setSupportOverlay } from '../../lib/supportRuntime';
import Button from '../ui/Button';
import {
  loadPaperSeedDraft,
  loadTemplateDraft,
  type ChatDigestDraft,
} from '../../lib/chatProjectCreation';
import { getSourceVenueLabels } from '../../lib/sourceHelpers';
import { fetchCurrentUserSourceAccess } from '../../lib/sourceAccess';
import {
  isSourceEntitlementRequiredError,
  sourceEntitlementMessage,
} from '../../lib/sourceAccessDecision';
import { ForwardedOnboardingStepper } from '../onboarding/ForwardedOnboardingShared';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { useLingui } from '@lingui/react';

interface Message {
  text: string | MessageDescriptor;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

type DigestData = ChatDigestDraft;
type ChatModalVariant = 'default' | 'forwarded';

interface ChatModalProps {
  visible: boolean;
  onClose: () => void;
  onComplete: (data: DigestData, context?: {
    chatSessionId: string | null;
    seedPaperId: string | null;
  }) => Promise<void>;
  templateProjectId?: string;
  seedPaperId?: string;
  entrySurface?: string;
  variant?: ChatModalVariant;
  forwardedContext?: {
    importCount: number;
    importedPaperCount: number;
  };
}

const MAX_TURNS = 15;
const SENTRY_TAG_VALUE_LIMIT = 200;
const MAX_CHAT_EDGE_FUNCTION_ATTEMPTS = 3;
const CHAT_EDGE_FUNCTION_RETRY_DELAY_MS = 1000;
const CHAT_EDGE_FUNCTION_FAILURE_MESSAGE = msg({
  id: 'discovery.chat.connectionFailure',
  message: 'Unexpected error. Please reload the page and try again, or email us at hello@paperzilla.ai',
});
const SEED_VALIDATION_WARNING = msg({
  id: 'discovery.chat.seedValidationWarning',
  message: "I loaded the digest template, but I couldn't verify the starting paper, so it won't be added automatically. You can continue with the template or go back and try again.",
});

const THINKING_PLACEHOLDERS = [
  msg({ id: 'discovery.chat.thinking.literature', message: 'Consulting the literature gods...' }),
  msg({ id: 'discovery.chat.thinking.crossReferencing', message: 'Cross-referencing 47 million papers...' }),
  msg({ id: 'discovery.chat.thinking.teachingRobot', message: 'Teaching a robot your taste in papers...' }),
  msg({ id: 'discovery.chat.thinking.intellectualVibe', message: 'Pondering your intellectual vibe...' }),
  msg({ id: 'discovery.chat.thinking.researchAura', message: 'Decoding your research aura...' }),
  msg({ id: 'discovery.chat.thinking.abstracts', message: 'Sifting through mountains of abstracts...' }),
  msg({ id: 'discovery.chat.thinking.hIndex', message: 'Channeling your inner h-index...' }),
];

const DEFAULT_WELCOME_MESSAGE = msg({
  id: 'discovery.chat.welcome',
  message: 'Great, let’s build your project.\n\nShare what you’re focused on (research field, methods, keywords, and what to ignore). I’ll draft it, and you can always change it later.',
});
const FORWARDED_WELCOME_MESSAGE = msg({
  id: 'discovery.chat.forwardedWelcome',
  message: 'Your alert is connected.\n\nTell me the research topic you want Paperzilla to keep from all sources. Share the field, methods, keywords, and what to ignore, and I’ll draft it.',
});

const truncateSentryTagValue = (value?: string | null): string => {
  if (!value) {
    return 'none';
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return 'none';
  }

  if (normalizedValue.length <= SENTRY_TAG_VALUE_LIMIT) {
    return normalizedValue;
  }

  return `${normalizedValue.slice(0, SENTRY_TAG_VALUE_LIMIT - 3)}...`;
};

const buildChatEdgeFunctionErrorLabel = (
  errorCode?: string | null,
  functionErrorMessage?: string | null,
): string => {
  if (errorCode && functionErrorMessage) {
    return `${errorCode} (${functionErrorMessage})`;
  }

  // i18n-ignore: technical-token -- diagnostic Sentry tag, never rendered to users.
  return errorCode || functionErrorMessage || 'Unknown';
};

const wait = (ms: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

// Chip component for tags
const Chip: React.FC<{ label: string; variant?: 'default' | 'category' }> = ({ label, variant = 'default' }) => (
  <View style={[
    chipStyles.chip,
    variant === 'category' && chipStyles.categoryChip
  ]}>
    <Text style={[
      chipStyles.chipText,
      variant === 'category' && chipStyles.categoryChipText
    ]}>
      {label}
    </Text>
  </View>
);

const chipStyles = StyleSheet.create({
  chip: {
    backgroundColor: colors.bgWeak,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    marginRight: 6,
    marginBottom: 6,
  },
  categoryChip: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  chipText: {
    fontSize: 13,
    color: colors.text700,
  },
  categoryChipText: {
    fontSize: 12,
    color: colors.text500,
  },
});

const ProposalSources: React.FC<{
  categories: ChatDigestDraft['categories'];
  testID: string;
}> = ({ categories, testID }) => {
  const sourceKey = [...new Set(categories.map(category => category.source_id))].join(',');
  const [sourceNames, setSourceNames] = useState(() => getSourceVenueLabels(categories.map(category => category.source_id)));
  useEffect(() => {
    let active = true;
    const sourceIds = sourceKey.split(',').filter(Boolean).map(Number);
    const knownNames = getSourceVenueLabels(sourceIds);
    setSourceNames(knownNames);
    if (knownNames.length < sourceIds.length) {
      // Resolves new source IDs on chat-only onboarding, before opening the picker.
      fetchCurrentUserSourceAccess().then(() => {
        if (active) setSourceNames(getSourceVenueLabels(sourceIds));
      }).catch(error => console.warn('Could not load source labels:', error));
    }
    return () => { active = false; };
  }, [sourceKey]);

  if (sourceNames.length === 0) {
    return null;
  }

  return (
    <View style={styles.digestSection} testID={testID}>
      <Text style={styles.digestSectionHeader}><Trans id="discovery.chat.sources" comment="UI copy in components/discovery/Chat.tsx">Sources</Trans></Text>
      <View style={styles.chipContainer}>
        {sourceNames.map((sourceName) => (
          <Chip key={sourceName} label={sourceName} />
        ))}
      </View>
    </View>
  );
};

// Animated typing dots component
const TypingDots = () => {
  const dot1 = useRef(new Animated.Value(0.3)).current;
  const dot2 = useRef(new Animated.Value(0.3)).current;
  const dot3 = useRef(new Animated.Value(0.3)).current;

  useEffect(() => {
    const animateDot = (dot: Animated.Value, delay: number) => {
      return Animated.loop(
        Animated.sequence([
          Animated.delay(delay),
          Animated.timing(dot, {
            toValue: 1,
            duration: 300,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(dot, {
            toValue: 0.3,
            duration: 300,
            useNativeDriver: Platform.OS !== 'web',
          }),
        ])
      );
    };

    const animation = Animated.parallel([
      animateDot(dot1, 0),
      animateDot(dot2, 150),
      animateDot(dot3, 300),
    ]);

    animation.start();
    return () => animation.stop();
  }, [dot1, dot2, dot3]);

  return (
    <View style={typingStyles.container}>
      <Animated.View style={[typingStyles.dot, { opacity: dot1 }]} />
      <Animated.View style={[typingStyles.dot, { opacity: dot2 }]} />
      <Animated.View style={[typingStyles.dot, { opacity: dot3 }]} />
    </View>
  );
};

const typingStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primaryGreen,
  },
});

// Animated send button that transforms into a spinner while thinking
const AnimatedSendButton: React.FC<{
  isTyping: boolean;
  disabled: boolean;
  onPress: () => void;
  testID?: string;
}> = ({ isTyping, disabled, onPress, testID }) => {
  const rotation = useRef(new Animated.Value(0)).current;
  const pulseScale = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (isTyping) {
      // Continuous rotation animation
      const rotateAnimation = Animated.loop(
        Animated.timing(rotation, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: Platform.OS !== 'web',
        })
      );

      // Subtle pulse animation
      const pulseAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseScale, {
            toValue: 1.08,
            duration: 600,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(pulseScale, {
            toValue: 1,
            duration: 600,
            useNativeDriver: Platform.OS !== 'web',
          }),
        ])
      );

      rotateAnimation.start();
      pulseAnimation.start();

      return () => {
        rotateAnimation.stop();
        pulseAnimation.stop();
        rotation.setValue(0);
        pulseScale.setValue(1);
      };
    } else {
      rotation.setValue(0);
      pulseScale.setValue(1);
    }
  }, [isTyping, rotation, pulseScale]);

  const spin = rotation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled || isTyping}
      activeOpacity={0.7}
      testID={testID}
    >
      <Animated.View
        style={[
          sendButtonStyles.button,
          isTyping && sendButtonStyles.buttonThinking,
          !isTyping && disabled && sendButtonStyles.buttonDisabled,
          isTyping && { transform: [{ scale: pulseScale }] },
        ]}
      >
        {isTyping ? (
          <Animated.View style={{ transform: [{ rotate: spin }] }}>
            <FontAwesome6 name="circle-notch" size={18} color="#fff" />
          </Animated.View>
        ) : (
          <FontAwesome6 name="paper-plane" size={18} color="#fff" />
        )}
      </Animated.View>
    </TouchableOpacity>
  );
};

const sendButtonStyles = StyleSheet.create({
  button: {
    position: 'absolute',
    right: 10,
    bottom: 10,
    backgroundColor: colors.zillaGreen,
    width: 38,
    height: 38,
    borderRadius: 19,
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS !== 'web' && {
      shadowColor: colors.zillaGreen,
      shadowOpacity: 0.3,
      shadowRadius: 6,
      shadowOffset: { width: 0, height: 2 },
      elevation: 3,
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: '0px 2px 6px rgba(34,197,94,0.3)',
    }),
  },
  buttonThinking: {
    backgroundColor: colors.primaryGreen,
    ...(Platform.OS !== 'web' && {
      shadowColor: colors.primaryGreen,
      shadowOpacity: 0.4,
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: '0px 2px 6px rgba(22,163,74,0.4)',
    }),
  },
  buttonDisabled: {
    backgroundColor: colors.mediumGray,
    opacity: 0.4,
    ...(Platform.OS !== 'web' && {
      shadowOpacity: 0,
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: 'none',
    }),
  },
});

// Animated thinking text overlay with gentle pulse
const AnimatedThinkingText: React.FC<{
  text: string;
  visible: boolean;
}> = ({ text, visible }) => {
  const opacity = useRef(new Animated.Value(0)).current;
  const textOpacity = useRef(new Animated.Value(0.6)).current;

  useEffect(() => {
    if (visible) {
      // Fade in
      Animated.timing(opacity, {
        toValue: 1,
        duration: 200,
        useNativeDriver: Platform.OS !== 'web',
      }).start();

      // Gentle text pulse
      const pulseAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(textOpacity, {
            toValue: 1,
            duration: 1200,
            useNativeDriver: Platform.OS !== 'web',
          }),
          Animated.timing(textOpacity, {
            toValue: 0.5,
            duration: 1200,
            useNativeDriver: Platform.OS !== 'web',
          }),
        ])
      );
      pulseAnimation.start();

      return () => {
        pulseAnimation.stop();
      };
    } else {
      opacity.setValue(0);
      textOpacity.setValue(0.6);
    }
  }, [visible, opacity, textOpacity]);

  if (!visible) return null;

  return (
    <Animated.View style={[thinkingTextStyles.container, { opacity }]}>
      <Animated.Text style={[thinkingTextStyles.text, { opacity: textOpacity }]}>
        {text}
      </Animated.Text>
    </Animated.View>
  );
};

const thinkingTextStyles = StyleSheet.create({
  container: {
    position: 'absolute',
    left: 16,
    right: 56,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  text: {
    fontSize: 15,
    fontStyle: 'italic',
    color: colors.primaryGreen,
    letterSpacing: 0.3,
  },
});

const ChatModal: React.FC<ChatModalProps> = ({
  visible,
  onClose,
  onComplete,
  templateProjectId,
  seedPaperId,
  entrySurface,
  variant = 'default',
  forwardedContext,
}) => {
  const { i18n } = useLingui();
  const { width } = useWindowDimensions();
  const isSm = width <= 640;
  const isMac = Platform.OS === 'web' && typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);
  const isForwardedVariant = variant === 'forwarded';
  const headerTitle = i18n._(isForwardedVariant
    ? msg({ id: 'discovery.chat.forwardedTitle', message: 'Set up your research topic' })
    : msg({ id: 'discovery.chat.title', message: 'Set Up Your Research Topic Feed' }));
  const initialBotMessage = isForwardedVariant ? FORWARDED_WELCOME_MESSAGE : DEFAULT_WELCOME_MESSAGE;
  const forwardedSummary = isForwardedVariant && forwardedContext
    ? i18n._({
      ...msg({
        id: 'discovery.chat.forwardedSummary',
        message: '{count, plural, one {# imported paper} other {# imported papers}}. Paperzilla will add them to your topic and keep the original forwarded emails visible in Inbox.',
      }),
      values: { count: forwardedContext.importedPaperCount },
    })
    : null;
  const resolveMessageText = React.useCallback(
    (value: Message['text']) => typeof value === 'string' ? value : i18n._(value),
    [i18n],
  );

  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [inputHeight, setInputHeight] = useState(44);
  const [isTyping, setIsTyping] = useState(false);
  const [isSubmittingCompletion, setIsSubmittingCompletion] = useState(false);
  const [projectCreationError, setProjectCreationError] = useState<ProjectLimitReachedError | null>(null);
  const [turnCount, setTurnCount] = useState(0);
  const [pendingDigestData, setPendingDigestData] = useState<DigestData | null>(null);
  const [lastProposalData, setLastProposalData] = useState<DigestData | null>(null);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const scrollViewRef = useRef<ScrollViewInstance>(null);
  const sessionIdRef = useRef<string | null>(null);
  const validatedSeedPaperIdRef = useRef<string | null>(null);
  // Keep the editing baseline even while its preview is hidden or a request retries.
  const currentProposalRef = useRef<DigestData | null>(null);
  const inputRef = useRef<TextInputInstance>(null);
  const ensureSessionPromiseRef = useRef<Promise<boolean> | null>(null);

  const updateFunnelParam = (isOpen: boolean) => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (isOpen) {
      url.searchParams.set('funnel', 'true');
    } else {
      url.searchParams.delete('funnel');
    }
    window.history.replaceState({}, '', url.toString());
  };

  const ensureSessionReady = async () => {
    if (isSessionReady) return true;
    if (ensureSessionPromiseRef.current) return ensureSessionPromiseRef.current;

    ensureSessionPromiseRef.current = ensureDiscoverySession()
      .then(() => {
        setIsSessionReady(true);
        return true;
      })
      .catch(err => {
        console.error('Failed to create early session:', err);
        reportChatSessionCreationError(err, sessionIdRef.current, Sentry.captureException);
        setIsSessionReady(false);
        setMessages(prev => [
          ...prev,
          {
            text: msg({
              id: 'discovery.chat.sessionStartFailure',
              message: "We couldn't start your session. Please reload and try again.",
            }),
            sender: 'bot',
            timestamp: new Date()
          }
        ]);
        return false;
      })
      .finally(() => {
        ensureSessionPromiseRef.current = null;
      });

    return ensureSessionPromiseRef.current;
  };

  // Initialize bot with welcome message
  useEffect(() => {
    let isCancelled = false;

    const preloadDraft = async () => {
      if (!templateProjectId && !seedPaperId) {
        return;
      }

      setIsTyping(true);
      try {
        let draft: ChatDigestDraft;
        let validatedSeedPaperId: string | null = null;
        let seedValidationWarning: MessageDescriptor | null = null;
        if (templateProjectId) {
          draft = await loadTemplateDraft(templateProjectId);
          if (seedPaperId) {
            try {
              const seedDraft = await loadPaperSeedDraft(seedPaperId);
              validatedSeedPaperId = seedPaperId;
              if (draft.categories.length === 0 && seedDraft.categories.length > 0) {
                draft = { ...draft, categories: seedDraft.categories };
              }
            } catch (seedError) {
              seedValidationWarning = SEED_VALIDATION_WARNING;
              console.error('❌ [ChatModal] Failed to validate starting paper:', seedError);
            }
          }
        } else {
          draft = await loadPaperSeedDraft(seedPaperId!);
          validatedSeedPaperId = seedPaperId!;
        }
        if (isCancelled) return;

        validatedSeedPaperIdRef.current = validatedSeedPaperId;
        currentProposalRef.current = draft;
        const hasUsableCategories = draft.categories.length > 0;
        setLastProposalData(null);
        setPendingDigestData(hasUsableCategories ? draft : null);
        setMessages(prev => [
          ...prev,
          {
            text: hasUsableCategories
              ? templateProjectId
                ? msg({ id: 'discovery.chat.templatePreloaded', message: 'I preloaded this from the template. Review it, then create the project or change anything you want.' })
                : msg({ id: 'discovery.chat.paperDrafted', message: 'I used this paper to draft a research digest. Review it, then create the project or change anything you want.' })
              : templateProjectId
                ? msg({ id: 'discovery.chat.templateNeedsCategory', message: 'I loaded the template, but it does not have a usable research category yet. What field or topic should this digest cover?' })
                : msg({ id: 'discovery.chat.paperNeedsCategory', message: 'I found the starting paper, but it does not have a usable research category yet. What field or topic should this digest cover?' }),
            sender: 'bot',
            timestamp: new Date()
          },
          ...(seedValidationWarning ? [{
            text: seedValidationWarning,
            sender: 'bot' as const,
            timestamp: new Date(),
          }] : [])
        ]);
      } catch (err) {
        if (isCancelled) return;
        validatedSeedPaperIdRef.current = null;
        console.error('❌ [ChatModal] Failed to preload digest draft:', err);
        setMessages(prev => [
          ...prev,
          {
            text: msg({
              id: 'discovery.chat.preloadFailure',
              message: "I couldn't preload that research context right now, but we can still set this up quickly from scratch.",
            }),
            sender: 'bot',
            timestamp: new Date()
          }
        ]);
      } finally {
        if (!isCancelled) {
          setIsTyping(false);
        }
      }
    };

    if (visible) {
      updateFunnelParam(true);
      // Generate a new session ID for this chat session
      sessionIdRef.current = crypto.randomUUID();
      validatedSeedPaperIdRef.current = null;
      currentProposalRef.current = null;
      setIsSessionReady(false);

      trackFunnelEvent('chat_opened', {
        chat_session_id: sessionIdRef.current,
        source: entrySurface || 'chat_modal',
        entry_surface: entrySurface || 'home_chat_modal',
        entry_path: Platform.OS === 'web' && typeof window !== 'undefined'
          ? `${window.location.pathname}${window.location.search}`
          : '/',
        ...(templateProjectId ? { template_project_id: templateProjectId } : {}),
      });

      // Create anonymous session early when chat opens
      // This gives time for the JWT to propagate before project creation
      ensureSessionReady();

      setMessages([
        {
          text: initialBotMessage,
          sender: 'bot',
          timestamp: new Date()
        }
      ]);
      setConversationHistory([]);
      setInputText('');
      setTurnCount(0);
      setPendingDigestData(null);
      setLastProposalData(null);
      setIsSubmittingCompletion(false);
      setProjectCreationError(null);

      preloadDraft();

      // Auto-focus input on web
      if (Platform.OS === 'web') {
        setTimeout(() => inputRef.current?.focus(), 100);
      }

    } else {
      updateFunnelParam(false);
    }

    return () => {
      isCancelled = true;
    };
  }, [visible, templateProjectId, seedPaperId, entrySurface, initialBotMessage]);

  // Keep the selected support launcher clear of the discovery modal.
  useEffect(() => {
    setSupportOverlay('discovery', visible);
    return () => setSupportOverlay('discovery', false);
  }, [visible]);

  // Handle sending messages
  const handleSend = async () => {
    if (!inputText.trim()) return;
    if (isTyping) return;

    const sessionOk = await ensureSessionReady();
    if (!sessionOk) return;

    trackFunnelEvent('chat_message_sent', {
      chat_session_id: sessionIdRef.current,
      turn: turnCount + 1,
      message_length: inputText.trim().length,
    });

    // Clear any pending digest data (user is continuing to refine)
    if (pendingDigestData) {
      setLastProposalData(pendingDigestData);
      setPendingDigestData(null);
    }

    // Add user message
    const userMessage: Message = {
      text: inputText.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputText('');
    setInputHeight(44); // Reset input height

    // Process user response
    processUserResponse(inputText.trim());
  };

  // Handle keyboard shortcuts (cmd/ctrl + enter to send)
  const handleKeyPress = (e: any) => {
    if (Platform.OS === 'web') {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSend();
      }
    }
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  }, [messages, pendingDigestData, lastProposalData]);

  const archivedProposalAnchorIndex = React.useMemo(() => {
    if (!lastProposalData || pendingDigestData) {
      return -1;
    }

    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (
        message.sender === 'bot'
        && resolveMessageText(message.text).startsWith('Sure - what should I change?')
      ) {
        return index;
      }
    }

    return -1;
  }, [messages, lastProposalData, pendingDigestData, resolveMessageText]);

  // Handle Review button click
  const handleReview = async () => {
    if (!pendingDigestData || isSubmittingCompletion || projectCreationError) {
      return;
    }

    const sessionOk = await ensureSessionReady();
    if (!sessionOk) {
      return;
    }

    trackFunnelEvent('chat_set_up_delivery_clicked', {
      chat_session_id: sessionIdRef.current,
      turn_count: turnCount,
    });

    setIsSubmittingCompletion(true);
    try {
      await onComplete(pendingDigestData, {
        chatSessionId: sessionIdRef.current,
        seedPaperId: validatedSeedPaperIdRef.current,
      });
      onClose();
    } catch (error) {
      console.error('❌ [ChatModal] Failed to create project:', error);
      const isProjectLimitError = isProjectLimitReachedError(error);
      const creationError = isProjectLimitError ? toProjectLimitReachedError(error) : null;
      setProjectCreationError(creationError);
      const isSourceEntitlementError = isSourceEntitlementRequiredError(error);
      const userMessage: Message['text'] = isProjectLimitError
        ? buildProjectLimitMessage(creationError?.snapshot)
        : isSourceEntitlementError
          ? sourceEntitlementMessage()
          : msg({ id: 'discovery.chat.createFailure', message: "I couldn't create the project right now. Please try again." });

      if (!isProjectLimitError && !isSourceEntitlementError) {
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { context: 'chat_complete_to_delivery' },
          extra: {
            chat_session_id: sessionIdRef.current,
            turn: turnCount,
          },
        });
      }

      setMessages(prev => [
        ...prev,
        {
          text: userMessage,
          sender: 'bot',
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsSubmittingCompletion(false);
    }
  };

  const handleEdit = () => {
    if (!pendingDigestData || isSubmittingCompletion) {
      return;
    }

    trackFunnelEvent('chat_change_clicked', {
      chat_session_id: sessionIdRef.current,
      turn_count: turnCount,
    });

    setLastProposalData(pendingDigestData);
    setPendingDigestData(null);
    const editMessage: Message = {
      text: msg({
        id: 'discovery.chat.changePrompt',
        message: 'Sure - what should I change? You can add, remove, or refine anything.',
      }),
      sender: 'bot',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, editMessage]);
  };

  const handleModalClose = () => {
    trackFunnelEvent('chat_closed', {
      chat_session_id: sessionIdRef.current,
      turn_count: turnCount,
      had_summary: Boolean(pendingDigestData || lastProposalData),
      reason: 'user_closed_modal',
    });

    onClose();
  };

  // Process user response via edge function
  const processUserResponse = async (
    message: string,
    options: {
      attempt?: number;
      hasRetriedAuth?: boolean;
    } = {},
  ) => {
    const attempt = options.attempt ?? 1;
    const hasRetriedAuth = options.hasRetriedAuth ?? false;
    const isRetry = attempt > 1 || hasRetriedAuth;
    setIsTyping(true);

    try {
      // Build conversation history
      const newHistory: ConversationMessage[] = [
        ...conversationHistory,
        { role: 'user', content: message }
      ];

      // Check turn limit (only on first attempt, not retries)
      if (!isRetry) {
        const newTurnCount = turnCount + 1;
        setTurnCount(newTurnCount);

        if (newTurnCount >= MAX_TURNS) {
          const limitMessage: Message = {
            text: msg({
              id: 'discovery.chat.conversationLimit',
              message: "We've reached the conversation limit for now. Tell me the most important change, and we'll refine from there.",
            }),
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, limitMessage]);
          setIsTyping(false);
          return;
        }

        // Persist user message immediately so it's not lost on error
        setConversationHistory(newHistory);
      }

      // Call edge function
      const journeyId = getCurrentJourneyId();
      const response = await supabase.functions.invoke('chat2digest', {
        body: {
          messages: newHistory,
          ...(currentProposalRef.current ? { current_proposal: currentProposalRef.current } : {}),
          session_id: sessionIdRef.current,
          ...(journeyId ? { journey_id: journeyId } : {}),
        }
      });

      const data = response.data;
      const functionError = response.error;

      // Handle error responses from edge function
      if (functionError || data?.error) {
        const functionErrorMessage = functionError?.message?.trim();
        const sentryErrorLabel = buildChatEdgeFunctionErrorLabel(data?.error, functionErrorMessage);
        const isTransportFailure = Boolean(functionError);

        // Log details for debugging
        if (data?.details) {
          console.error('Edge function error details:', data.details);
        }

        // Handle expired session - try to refresh and retry once
        if (data?.error === 'AUTH_EXPIRED' && !hasRetriedAuth) {
          console.log('Session expired, attempting refresh...');
          const { error: refreshError } = await supabase.auth.refreshSession();

          if (!refreshError) {
            console.log('Session refreshed, retrying request...');
            return processUserResponse(message, { attempt, hasRetriedAuth: true });
          }
          console.error('Session refresh failed:', refreshError);
        }

        if (isTransportFailure && attempt < MAX_CHAT_EDGE_FUNCTION_ATTEMPTS) {
          console.warn(`Chat edge function attempt ${attempt} failed. Retrying...`, {
            functionError: functionErrorMessage,
            sessionId: sessionIdRef.current,
          });
          await wait(CHAT_EDGE_FUNCTION_RETRY_DELAY_MS);
          return processUserResponse(message, {
            attempt: attempt + 1,
            hasRetriedAuth,
          });
        }

        // Report backend errors to Sentry (not user errors like AUTH_EXPIRED, VALIDATION_ERROR)
        const backendErrorCodes = ['INTERNAL_ERROR', 'LLM_UNAVAILABLE'];
        if (backendErrorCodes.includes(data?.error) || isTransportFailure) {
          Sentry.captureException(new Error(`Chat edge function error: ${sentryErrorLabel}`), {
            tags: {
              context: 'chat_edge_function',
              edge_function: 'chat2digest',
              error_code: data?.error || 'none',
              function_error_message: truncateSentryTagValue(functionErrorMessage),
              retry_exhausted: isTransportFailure ? 'true' : 'false',
            },
            extra: {
              attempt,
              maxAttempts: MAX_CHAT_EDGE_FUNCTION_ATTEMPTS,
              errorCode: data?.error,
              details: data?.details,
              functionError: functionErrorMessage,
              sessionId: sessionIdRef.current,
            }
          });
        }

        // Use userMessage from structured error response, or fallback
        const userMessage: Message['text'] = isTransportFailure
          ? CHAT_EDGE_FUNCTION_FAILURE_MESSAGE
          : data?.userMessage || msg({
            id: 'discovery.chat.genericFailure',
            message: 'Something went wrong. Please try again, or reload the page if the problem persists.',
          });

        const errorMessage: Message = {
          text: userMessage,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);

        return;
      }

      if (!data) {
        throw new Error('No response from server');
      }

      // Add assistant response to conversation history
      setConversationHistory(prev => [
        ...prev,
        { role: 'assistant', content: data.response.message }
      ]);

      trackFunnelEvent('chat_message_received', {
        chat_session_id: sessionIdRef.current,
        turn: turnCount,
        message_length: data.response?.message?.length || 0,
        status: data.status,
      });

      // If complete, show confirmation card (skip the bot message to avoid duplicate summaries)
      if (data.status === 'complete' && data.digestData) {
        trackFunnelEvent('chat_summary_ready', {
          chat_session_id: sessionIdRef.current,
          turn_count: newHistory.filter((conversationTurn) => conversationTurn.role === 'user').length,
          categories_count: data.digestData.categories?.length || 0,
          include_keywords_count: data.digestData.include?.length || 0,
          exclude_keywords_count: data.digestData.exclude?.length || 0,
          interest_length: data.digestData.interestText?.length || 0,
        });
        if (Array.isArray(data.digestData.categories) && data.digestData.categories.length > 0) {
          setLastProposalData(null);
          setPendingDigestData(data.digestData);
          currentProposalRef.current = data.digestData;
        } else {
          setPendingDigestData(null);
          setMessages(prev => [
            ...prev,
            {
              text: msg({
                id: 'discovery.chat.categoryRequired',
                message: 'I still need at least one research field or topic before I can create this digest. What field should it cover?',
              }),
              sender: 'bot',
              timestamp: new Date(),
            },
          ]);
        }
      } else {
        // Add bot message to UI (only when not showing confirmation card)
        const botMessage: Message = {
          text: data.response.message,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
      }

    } catch (err: any) {
      const error = err instanceof Error ? err : new Error(String(err));

      if (attempt < MAX_CHAT_EDGE_FUNCTION_ATTEMPTS) {
        console.warn(`Chat edge function threw on attempt ${attempt}. Retrying...`, {
          error: error.message,
          sessionId: sessionIdRef.current,
        });
        await wait(CHAT_EDGE_FUNCTION_RETRY_DELAY_MS);
        return processUserResponse(message, {
          attempt: attempt + 1,
          hasRetriedAuth,
        });
      }

      // Network failure - no response from server
      console.error('Network error:', error);

      // Track network error once retries are exhausted
      Sentry.captureException(error, {
        tags: {
          context: 'chat_network_error',
          edge_function: 'chat2digest',
          retry_exhausted: 'true',
        },
        extra: {
          attempt,
          maxAttempts: MAX_CHAT_EDGE_FUNCTION_ATTEMPTS,
          chat_session_id: sessionIdRef.current,
          turn: turnCount,
        },
      });

      const errorMessage: Message = {
        text: CHAT_EDGE_FUNCTION_FAILURE_MESSAGE,
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <Modal
      animationType="slide"
      transparent={true}
      visible={visible}
      onRequestClose={handleModalClose}
      testID="chat-modal"
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.modalOverlay}
      >
        <View style={[
          styles.modalContent,
          isSm && styles.modalContentSm
        ]}>
          <View style={[
            styles.header,
            !isForwardedVariant && isSm && styles.headerSm,
            isForwardedVariant && styles.headerForwarded,
          ]}>
            <View style={[
              styles.headerTextContainer,
              isForwardedVariant && styles.headerTextContainerForwarded,
            ]}>
              {isForwardedVariant ? (
                <View style={styles.forwardedStepperWrap}>
                  <ForwardedOnboardingStepper />
                </View>
              ) : null}
              <View style={styles.headerCopyGroup}>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.headerTitle,
                    !isForwardedVariant && isSm && styles.headerTitleSm,
                    !isForwardedVariant && width <= 360 && styles.headerTitleXs,
                  ]}
                  testID="chat-modal-title"
                >
                  {headerTitle}
                </Text>
                {!isForwardedVariant ? (
                  <Text style={styles.headerSubtitle}><Trans id="discovery.chat.oneSetupOngoingFeedUpdatesDigestEmails" comment="UI copy in components/discovery/Chat.tsx">One setup. Ongoing feed updates + digest emails.</Trans></Text>
                ) : null}
              </View>
              {forwardedSummary ? (
                <Text style={styles.forwardedSummary} testID="forwarded-chat-summary">
                  {forwardedSummary}
                </Text>
              ) : null}
            </View>
            <TouchableOpacity
              onPress={handleModalClose}
              style={[
                styles.closeButton,
                !isForwardedVariant && isSm && styles.closeButtonSm,
                isForwardedVariant && styles.closeButtonTop,
              ]}
              testID="chat-close-button"
            >
              <FontAwesome6 name="xmark" size={20} color={colors.text500} />
            </TouchableOpacity>
          </View>

          <ScrollView
            ref={scrollViewRef}
            style={styles.messagesContainer}
            contentContainerStyle={styles.messagesContent}
          >
            {messages.map((chatMessage, index) => (
              <React.Fragment key={index}>
                {index === archivedProposalAnchorIndex && lastProposalData && !pendingDigestData && (
                  <View style={styles.confirmationBubble}>
                    <Text style={styles.lastProposalLabel}><Trans id="discovery.chat.lastProposal" comment="UI copy in components/discovery/Chat.tsx">Last proposal</Trans></Text>

                    {lastProposalData.interestText && (
                      <Text style={styles.digestIntroText}>{lastProposalData.interestText}</Text>
                    )}

                    {lastProposalData.include.length > 0 && (
                      <View style={styles.digestSection}>
                        <Text style={styles.digestSectionHeader}><Trans id="discovery.chat.keywordsWeLlBoost" comment="UI copy in components/discovery/Chat.tsx">Keywords we'll boost</Trans></Text>
                        <View style={styles.chipContainer}>
                          {lastProposalData.include.map((keyword: string, i: number) => (
                            <Chip key={i} label={keyword} />
                          ))}
                        </View>
                      </View>
                    )}

                    {lastProposalData.exclude.length > 0 && (
                      <View style={styles.digestSection}>
                        <Text style={styles.digestSectionHeader}><Trans id="discovery.chat.weLlSkip" comment="UI copy in components/discovery/Chat.tsx">We'll skip</Trans></Text>
                        <Text style={styles.digestExclusionText}>
                          {lastProposalData.exclude.join(', ')}
                        </Text>
                      </View>
                    )}

                    <ProposalSources
                      categories={lastProposalData.categories}
                      testID="chat-archived-proposal-sources"
                    />

                    {lastProposalData.categories.length > 0 && (
                      <View style={styles.digestSection}>
                        <Text style={styles.digestSectionHeader}><Trans id="discovery.chat.categories" comment="UI copy in components/discovery/Chat.tsx">Categories</Trans></Text>
                        <View style={styles.chipContainer}>
                          {lastProposalData.categories.map((cat, i: number) => (
                            <Chip key={i} label={cat.name} variant="category" />
                          ))}
                        </View>
                      </View>
                    )}
                  </View>
                )}

                <View
                  style={[
                    styles.messageBubble,
                    chatMessage.sender === 'user' ? styles.userMessage : styles.botMessage
                  ]}
                >
                  <Text style={[
                    styles.messageText,
                    chatMessage.sender === 'user' ? styles.userMessageText : styles.botMessageText
                  ]}>
                    {chatMessage.sender === 'bot'
                      ? (resolveMessageText(chatMessage.text).startsWith('🦖')
                        ? resolveMessageText(chatMessage.text)
                        : `🦖 ${resolveMessageText(chatMessage.text)}`)
                      : resolveMessageText(chatMessage.text)}
                  </Text>
                </View>
              </React.Fragment>
            ))}
            {isTyping && (
              <View style={[styles.messageBubble, styles.botMessage]}>
                <TypingDots />
              </View>
            )}
            {pendingDigestData && (
              <View style={styles.confirmationBubble}>
                {/* Interest description as intro */}
                {pendingDigestData.interestText && (
                  <Text style={styles.digestIntroText}>{pendingDigestData.interestText}</Text>
                )}

                {/* Keywords as chips */}
                {pendingDigestData.include.length > 0 && (
                  <View style={styles.digestSection}>
                    <Text style={styles.digestSectionHeader}><Trans id="discovery.chat.keywordsWeLlBoost" comment="UI copy in components/discovery/Chat.tsx">Keywords we'll boost</Trans></Text>
                    <View style={styles.chipContainer}>
                      {pendingDigestData.include.map((keyword: string, i: number) => (
                        <Chip key={i} label={keyword} />
                      ))}
                    </View>
                  </View>
                )}

                {/* Exclusions */}
                {pendingDigestData.exclude.length > 0 && (
                  <View style={styles.digestSection}>
                    <Text style={styles.digestSectionHeader}><Trans id="discovery.chat.weLlSkip" comment="UI copy in components/discovery/Chat.tsx">We'll skip</Trans></Text>
                    <Text style={styles.digestExclusionText}>
                      {pendingDigestData.exclude.join(', ')}
                    </Text>
                  </View>
                )}

                <ProposalSources
                  categories={pendingDigestData.categories}
                  testID="chat-proposal-sources"
                />

                {/* Categories as chips */}
                {pendingDigestData.categories.length > 0 && (
                  <View style={styles.digestSection}>
                    <Text style={styles.digestSectionHeader}><Trans id="discovery.chat.categories" comment="UI copy in components/discovery/Chat.tsx">Categories</Trans></Text>
                    <View style={styles.chipContainer}>
                      {pendingDigestData.categories.map((cat, i: number) => (
                        <Chip key={i} label={cat.name} variant="category" />
                      ))}
                    </View>
                  </View>
                )}

                {projectCreationError ? (
                  <ProjectCreationUpgradeBanner
                    snapshot={projectCreationError.snapshot}
                    message={buildProjectLimitMessage(projectCreationError.snapshot)}
                    dismissable={false}
                    testID="chat-project-creation-blocked"
                  />
                ) : <View style={styles.buttonRow}>
                  <Button
                    label={isSubmittingCompletion
                      ? msg({ id: 'discovery.chat.creating', message: 'Creating…' })
                      : msg({ id: 'discovery.chat.createProject', message: 'Create project' })}
                    variant="primary"
                    onPress={handleReview}
                    disabled={isSubmittingCompletion}
                    testID="chat-review-button"
                  />
                  <Button
                    label={msg({ id: 'discovery.chat.changeThis', message: 'Change this' })}
                    variant="secondary"
                    onPress={handleEdit}
                    disabled={isSubmittingCompletion}
                    testID="chat-edit-button"
                  />
                </View>}
                <View style={styles.reassuranceNotice}>
                  <FontAwesome6 name="circle-info" size={12} color={colors.text700} style={styles.reassuranceIcon} />
                  <Text style={styles.reassuranceText}><Trans id="discovery.chat.noNeedToPerfectThisNowYouCanEditCategories" comment="UI copy in components/discovery/Chat.tsx">
                    No need to perfect this now. You can edit categories, keywords, and description in detail after setup.
                  </Trans></Text>
                </View>
              </View>
            )}
          </ScrollView>

          {!pendingDigestData && (
            <View style={styles.inputWrapper}>
              <View style={styles.inputContainer}>
                <TextInput
                  ref={inputRef}
                  style={[
                    styles.input,
                    { height: Math.min(inputHeight, 120) },
                    isTyping && styles.inputDisabled
                  ]}
                  value={inputText}
                  onChangeText={setInputText}
                  placeholder={isTyping
                    ? ''
                    : Platform.OS === 'web'
                      ? i18n._({
                        ...msg({
                          id: 'discovery.chat.messagePlaceholderWithShortcut',
                          message: 'Type your message... ({key}+Enter to send)',
                        }),
                        values: { key: isMac ? 'Cmd' : 'Ctrl' },
                      })
                      : i18n._(msg({ id: 'discovery.chat.messagePlaceholder', message: 'Type your message...' }))
                  }
                  placeholderTextColor={colors.text500}
                  multiline
                  editable={!isTyping}
                  onContentSizeChange={(e) => {
                    setInputHeight(e.nativeEvent.contentSize.height + 24);
                  }}
                  onKeyPress={handleKeyPress}
                  testID="chat-message-input"
                />
                <AnimatedThinkingText
                  text={i18n._(THINKING_PLACEHOLDERS[Math.max(0, turnCount - 1) % THINKING_PLACEHOLDERS.length])}
                  visible={isTyping}
                />
                <AnimatedSendButton
                  isTyping={isTyping}
                  disabled={!inputText.trim()}
                  onPress={handleSend}
                  testID="chat-send-button"
                />
              </View>
            </View>
          )}
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 999999,
  },
  modalContent: {
    width: '80%',
    maxWidth: 1100,
    height: '80%',
    backgroundColor: 'white',
    borderRadius: 16,
    overflow: 'hidden',
  },
  modalContentSm: {
    width: '100%',
    height: '100%',
    borderRadius: 0,
    maxWidth: '100%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    backgroundColor: '#fff',
    ...(Platform.OS !== 'web' && {
      shadowColor: "#000",
      shadowOpacity: 0.06,
      shadowRadius: 8,
      shadowOffset: { width: 0, height: 2 },
      elevation: 2,
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: '0px 2px 8px rgba(0,0,0,0.06)',
    }),
    zIndex: 1,
  },
  headerSm: {
    gap: 8,
    padding: 12,
  },
  headerForwarded: {
    alignItems: 'flex-start',
  },
  headerTextContainer: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  headerTextContainerForwarded: {
    gap: 10,
    paddingRight: 12,
  },
  headerCopyGroup: {
    minWidth: 0,
    gap: 2,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text900,
  },
  headerTitleSm: {
    fontSize: 15,
    letterSpacing: -0.2,
  },
  headerTitleXs: {
    fontSize: 13,
  },
  headerSubtitle: {
    marginTop: 3,
    fontSize: 13,
    color: colors.text500,
    lineHeight: 16,
  },
  forwardedStepperWrap: {
    paddingTop: 2,
  },
  forwardedSummary: {
    fontSize: 13,
    lineHeight: 18,
    color: colors.text600,
    maxWidth: 680,
  },
  closeButton: {
    flexShrink: 0,
    padding: 10,
    borderRadius: 20,
    backgroundColor: colors.bgWeak,
  },
  closeButtonSm: {
    padding: 8,
  },
  closeButtonTop: {
    marginTop: 2,
    flexShrink: 0,
  },
  messagesContainer: {
    flex: 1,
    backgroundColor: '#fff',
    ...(Platform.OS === 'web' ? {
      scrollbarWidth: 'thin',
      scrollbarColor: `${colors.border} transparent`,
    } : {}),
  },
  messagesContent: {
    padding: 16,
    gap: 14,
  },
  messageBubble: {
    maxWidth: '80%',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 16,
    marginVertical: 4,
  },
  userMessage: {
    alignSelf: 'flex-end',
    backgroundColor: colors.zillaGreen,
    borderBottomRightRadius: 4,
    ...(Platform.OS !== 'web' && {
      shadowColor: "#000",
      shadowOpacity: 0.12,
      shadowRadius: 6,
      shadowOffset: { width: 0, height: 3 },
      elevation: 3,
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: '0px 3px 6px rgba(0,0,0,0.12)',
    }),
  },
  botMessage: {
    alignSelf: 'flex-start',
    backgroundColor: colors.bgWeak,
    borderBottomLeftRadius: 4,
    ...(Platform.OS !== 'web' && {
      shadowColor: "#000",
      shadowOpacity: 0.04,
      shadowRadius: 4,
      shadowOffset: { width: 0, height: 1 },
      elevation: 1,
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: '0px 1px 4px rgba(0,0,0,0.04)',
    }),
  },
  messageText: {
    fontSize: 15,
    lineHeight: 20,
  },
  userMessageText: {
    color: '#fff',
  },
  botMessageText: {
    color: colors.text900,
  },
  inputWrapper: {
    borderTopWidth: 0,
    backgroundColor: '#fff',
    padding: 16,
    ...(Platform.OS !== 'web' && {
      shadowColor: "#000",
      shadowOpacity: 0.06,
      shadowRadius: 8,
      shadowOffset: { width: 0, height: -2 },
      elevation: 2,
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: '0px -2px 8px rgba(0,0,0,0.06)',
    }),
  },
  inputContainer: {
    position: 'relative',
  },
  input: {
    borderWidth: 2,
    borderColor: colors.border,
    borderRadius: 16,
    paddingLeft: 16,
    paddingRight: 56,
    paddingTop: 12,
    paddingBottom: 12,
    fontSize: 15,
    color: colors.text900,
    backgroundColor: '#fff',
    ...(Platform.OS === 'web' ? { outlineWidth: 0 } : {}),
  },
  inputDisabled: {
    backgroundColor: colors.bgWeak,
    borderColor: colors.border,
  },
  confirmationBubble: {
    width: '100%',
    gap: 14,
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 16,
    ...(Platform.OS !== 'web' && {
      shadowColor: "#000",
      shadowOpacity: 0.08,
      shadowRadius: 8,
      shadowOffset: { width: 0, height: 2 },
      elevation: 2,
    }),
    ...(Platform.OS === 'web' && {
      boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
    }),
  },
  digestIntroText: {
    fontSize: 14,
    lineHeight: 21,
    color: colors.text700,
    marginBottom: 4,
  },
  digestSection: {
    marginTop: 12,
  },
  digestSectionHeader: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.text500,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  digestExclusionText: {
    fontSize: 14,
    lineHeight: 20,
    color: colors.text500,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 4,
  },
  reassuranceNotice: {
    marginTop: 4,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    backgroundColor: colors.bgWeak,
    borderColor: colors.border,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  reassuranceIcon: {
    marginTop: 2,
  },
  reassuranceText: {
    fontSize: 12,
    lineHeight: 18,
    color: colors.text700,
    flex: 1,
  },
  lastProposalLabel: {
    fontSize: 12,
    lineHeight: 16,
    color: colors.text500,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    fontWeight: '600',
  },
});

export default ChatModal;
